import { IReportStructure } from "dto/Reports/IReports";

// Model widoku raportu — port ReportStructureTree/GlobalReportVM z Magistratu:
// drzewo komórek → osób z licznikami, sumy komórek liczone z poddrzewa,
// zaznaczone osoby trafiają na wykres skumulowany.

export interface ReportSeries {
  key: string;
  label: string;
  color: string;
}

/** Wartości serii dla osoby (klucz serii → liczba). */
export type SeriesValues = Record<string, number>;

export interface ReportNode {
  key: string;
  name: string;
  subtitle?: string | null;
  isUser: boolean;
  userId?: string;
  parentKey: string | null;
  values: SeriesValues;
  total: number;
  children: ReportNode[];
}

export const SERIES_COLORS = [
  "#5d49c7",
  "#2e9e6b",
  "#e0902f",
  "#c94a4a",
  "#3b8fd9",
  "#8a6bbf",
  "#c7a23a",
  "#4aa3a3",
];

const sumValues = (nodes: ReportNode[], keys: string[]): SeriesValues => {
  const out: SeriesValues = {};
  for (const k of keys) out[k] = nodes.reduce((s, n) => s + (n.values[k] ?? 0), 0);
  return out;
};

const totalOf = (values: SeriesValues) =>
  Object.values(values).reduce((s, v) => s + v, 0);

/**
 * Buduje drzewo: komórki (z zagnieżdżeniem) → osoby; osoby bez komórki lądują
 * w korzeniu. Sumy komórek z poddrzewa (jak GetStructure w EPM).
 */
export const buildTree = (
  structure: IReportStructure,
  seriesKeys: string[],
  valuesByUser: Map<string, SeriesValues>,
): ReportNode[] => {
  const unitNodes = new Map<string, ReportNode>();
  for (const u of structure.Units) {
    unitNodes.set(u.Id, {
      key: `u:${u.Id}`,
      name: u.Symbol ? `${u.Symbol} — ${u.Name}` : u.Name,
      isUser: false,
      parentKey: u.ParentId ? `u:${u.ParentId}` : null,
      values: {},
      total: 0,
      children: [],
    });
  }

  const roots: ReportNode[] = [];

  for (const u of structure.Units) {
    const node = unitNodes.get(u.Id)!;
    const parent = u.ParentId ? unitNodes.get(u.ParentId) : undefined;
    (parent ? parent.children : roots).push(node);
  }

  for (const p of structure.People) {
    const values = valuesByUser.get(p.UserId) ?? {};
    const node: ReportNode = {
      key: `p:${p.UserId}`,
      name: p.Name || "(bez nazwy)",
      subtitle: p.PositionTitle,
      isUser: true,
      userId: p.UserId,
      parentKey: p.UnitId && unitNodes.has(p.UnitId) ? `u:${p.UnitId}` : null,
      values,
      total: totalOf(values),
      children: [],
    };
    const parent = p.UnitId ? unitNodes.get(p.UnitId) : undefined;
    (parent ? parent.children : roots).push(node);
  }

  const fill = (node: ReportNode) => {
    if (node.isUser) return;
    node.children.forEach(fill);
    node.values = sumValues(node.children, seriesKeys);
    node.total = totalOf(node.values);
    // Osoby wg malejącej sumy, komórki na końcu (jak listy EPM).
    node.children.sort((a, b) =>
      a.isUser !== b.isUser ? (a.isUser ? -1 : 1) : b.total - a.total || a.name.localeCompare(b.name, "pl"),
    );
  };
  roots.forEach(fill);
  roots.sort((a, b) =>
    a.isUser !== b.isUser ? (a.isUser ? -1 : 1) : b.total - a.total || a.name.localeCompare(b.name, "pl"),
  );
  return roots;
};

export const flatten = (nodes: ReportNode[]): ReportNode[] =>
  nodes.flatMap((n) => [n, ...flatten(n.children)]);

export const userKeysUnder = (node: ReportNode): string[] =>
  node.isUser ? [node.key] : node.children.flatMap(userKeysUnder);
